import type { TFunction } from 'i18next';
import { PaymentStatus, StripeRefundStatus } from '../models/RoomBooking';
import { isUserTutorBookingCancelledStatus } from './tutorBookingStatus';

const REFUND_BLOCKED_STATUSES: ReadonlySet<StripeRefundStatus> = new Set([
  StripeRefundStatus.SUCCEEDED,
  StripeRefundStatus.PENDING,
]);

export function getTutorPaymentStatusChipColor(status: string | null | undefined): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (!status) return 'default';
  switch (status.toUpperCase()) {
    case PaymentStatus.PAID:
      return 'success';
    case PaymentStatus.AMOUNT_BLOCKED:
    case PaymentStatus.BLOCK_REQUESTED:
      return 'warning';
    case PaymentStatus.CANCELLED:
      return 'error';
    case PaymentStatus.TO_BE_PAID_ON_SITE:
      return 'info';
    default:
      return 'default';
  }
}

export function getTutorRefundStatusChipColor(
  status: StripeRefundStatus | null | undefined,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (!status) return 'default';
  switch (status) {
    case StripeRefundStatus.SUCCEEDED:
      return 'success';
    case StripeRefundStatus.PENDING:
    case StripeRefundStatus.REQUIRES_ACTION:
      return 'warning';
    case StripeRefundStatus.FAILED:
    case StripeRefundStatus.CANCELED:
      return 'error';
    default:
      return 'default';
  }
}

/** User-facing refund chip copy — avoids ambiguous bare "Succeeded". */
export function getTutorRefundStatusChipLabel(
  status: StripeRefundStatus | string,
  t: TFunction,
): string {
  const normalized = String(status).toUpperCase() as StripeRefundStatus;
  if (normalized === StripeRefundStatus.SUCCEEDED) {
    return t('refundStatus.refunded', 'Refunded');
  }

  const statusKey = String(status).toLowerCase();
  return t('refundStatus.withLabel', '{{label}}: {{status}}', {
    label: t('refundStatus.label', 'Refund'),
    status: t(`refundStatus.${statusKey}`, String(status)),
  });
}

/** Manual Stripe refund / payment release for cancelled online tutor bookings. */
export function canRefundTutorBooking(booking: {
  status: string;
  paymentStatus?: PaymentStatus | string | null;
  stripeRefundStatus?: StripeRefundStatus | null;
}): boolean {
  if (!isUserTutorBookingCancelledStatus(booking.status)) {
    return false;
  }

  const paymentStatus = booking.paymentStatus?.toString().toUpperCase();
  if (
    paymentStatus !== PaymentStatus.PAID &&
    paymentStatus !== PaymentStatus.AMOUNT_BLOCKED &&
    paymentStatus !== PaymentStatus.BLOCK_REQUESTED
  ) {
    return false;
  }

  if (booking.stripeRefundStatus && REFUND_BLOCKED_STATUSES.has(booking.stripeRefundStatus)) {
    return false;
  }

  return true;
}
